import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  Stethoscope, CalendarCheck, Users, ShieldCheck, Bell, Repeat,
  ArrowRight, QrCode, CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { LegalDisclaimer } from "../components/LegalDisclaimer";

const HERO_IMG =
  "https://images.unsplash.com/photo-1620927894688-8bcade170468?auto=format&fit=crop&w=1200&q=80";

const FEATURES = [
  { icon: CalendarCheck, title: "Agende in tempo reale", text: "Consulta gli slot liberi dei medici e prenota in pochi secondi, senza telefonate." },
  { icon: Users, title: "Catalogo Medici", text: "Ricerca e filtra tutti i medici operativi nella provincia di Como per specializzazione." },
  { icon: Repeat, title: "Scambio appuntamenti", text: "Proponi lo scambio di uno slot con un altro informatore presso lo stesso medico." },
  { icon: Bell, title: "Notifiche multicanale", text: "Email e notifiche in-app per conferme, disdette e proposte di scambio." },
];

export default function Landing() {
  const navigate = useNavigate();
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-700 text-white">
              <Stethoscope className="h-5 w-5" />
            </div>
            <span className="font-heading text-lg font-800 text-slate-900">Agenda-ISF-COMO</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">
              Accedi
            </Button>
            <Button className="rounded-full bg-blue-700 hover:bg-blue-800" onClick={() => navigate("/registrati")} data-testid="nav-register-btn">
              Registrati
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="animate-fade-in-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1 text-xs font-600 text-blue-700">
              <ShieldCheck className="h-3.5 w-3.5" /> Piattaforma B2B · Provincia di Como
            </span>
            <h1 className="mt-5 font-heading text-4xl font-800 leading-tight text-slate-900 sm:text-5xl">
              La gestione delle agende tra <span className="text-blue-700">Medici</span> e{" "}
              <span className="text-blue-700">Informatori Scientifici</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-600">
              Prenota, gestisci e scambia appuntamenti in modo semplice e professionale.
              Un unico strumento per coordinare le visite degli ISF con i medici della provincia di Como.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-full bg-blue-700 px-6 hover:bg-blue-800" onClick={() => navigate("/registrati")} data-testid="hero-register-btn">
                Crea il tuo account <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full px-6" onClick={() => navigate("/login")} data-testid="hero-login-btn">
                Ho già un account
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
              {["Registrazione Medico o ISF", "Approvazione sicura", "Conforme GDPR"].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {t}
                </span>
              ))}
            </div>
          </div>
          <div className="relative animate-fade-in-up">
            <img
              src={HERO_IMG}
              alt="Vista aerea del Lago di Como e della città"
              className="aspect-[4/3] w-full rounded-2xl border border-slate-200 object-cover shadow-sm"
            />
            <div className="absolute -bottom-6 -left-6 hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:block">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                  <CalendarCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-700 text-slate-900">Appuntamento confermato</p>
                  <p className="text-xs text-slate-400">Notifica inviata a entrambe le parti</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-slate-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-lg font-800 text-slate-900 md:text-lg">Tutto ciò che serve per coordinare le visite</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">Strumenti pensati per lo standard della sanità digitale.</p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="rounded-xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-sm animate-fade-in-up"
                style={{ animationDelay: `${i * 70}ms` }}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-sky-100 text-blue-700">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading text-base font-700 text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-500">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QR */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-8 rounded-2xl border border-slate-200 bg-white p-8 lg:grid-cols-2 lg:p-12">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1 text-xs font-600 text-blue-700">
                <QrCode className="h-3.5 w-3.5" /> Accesso rapido
              </span>
              <h2 className="mt-4 font-heading text-lg font-800 text-slate-900">Inquadra e accedi alla piattaforma</h2>
              <p className="mt-2 max-w-md text-sm text-slate-500">
                Utilizza questo QR Code su materiali cartacei o promozionali: indirizza
                direttamente alla pagina di accesso e registrazione di Agenda-ISF-COMO.
              </p>
            </div>
            <div className="flex justify-center lg:justify-end">
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="landing-qrcode">
                <QRCodeCanvas value={appUrl} size={180} fgColor="#026AA2" level="M" includeMargin />
              </div>
            </div>
          </div>
        </div>
      </section>

      <LegalDisclaimer variant="footer" />
    </div>
  );
}
